import {IntlShape, defineMessages} from 'react-intl'

/**
 * Catálogo del Supervisor de telefonía (`/telephony-supervisor`): las pestañas
 * Live, Metrics, Call log, Dialer health, Reports y Campaigns, más la pestaña
 * de acceso a campañas.
 *
 * No se traduce:
 * - **PolicyDial, VICIdial, Twilio, PolicyTools**: productos.
 * - **dialer**: el equipo lo usa en inglés; "Dialer health" -> "Salud del dialer".
 *   En cambio **trunk** -> "troncal" y **drop** -> "caída" (glosario §17.14).
 * - **MONITOR / WHISPER / BARGE**: las tres etapas de supervisión de VICIdial;
 *   el equipo las usa en inglés y son las mismas en la consola del dialer.
 * - **Estados de agente** (`READY`, `INCALL`, `PAUSED`, `DISPO`…),
 *   **disposiciones** (`SALE`, `NANS`, `DROP`…) y **códigos de pausa**: los
 *   sirve VICIdial y se pintan en crudo.
 * - Nombres de campaña, de lista, de agente y números E.164.
 * - Los nombres de estado de EE. UU. de `US_STATE_NAMES` (topónimos).
 */
export const EM_DASH = '—'

/**
 * Etapa de supervisión, con el casing que se muestra en pantalla. No son
 * traducibles: son los nombres que usa VICIdial.
 */
export const STAGE_LABEL: Record<string, string> = {
  MONITOR: 'Monitor',
  WHISPER: 'Whisper',
  BARGE: 'Barge',
}

export const stageLabel = (stage: string) => STAGE_LABEL[stage] || stage

export const SUPERVISOR_MESSAGES = defineMessages({
  // ── Toasts y confirmaciones de la página ──
  toastUsageQueued: {
    id: 'telephony.supervisor.toast.usageQueued',
    defaultMessage:
      'Queued {days, plural, one {# day} other {# days}} — data is compiling in the background, refresh in ~1 min.',
  },
  toastUsageRebuildFailed: {
    id: 'telephony.supervisor.toast.usageRebuildFailed',
    defaultMessage: 'Could not rebuild usage data',
  },
  confirmAutorotate: {
    id: 'telephony.supervisor.confirm.autorotate',
    defaultMessage:
      'Enable automatic rotation? A nightly job will BUY replacement numbers for burned pool CIDs (this costs money). Only pool numbers are ever touched.',
  },
  autorotateFailed: {
    id: 'telephony.supervisor.autorotateFailed',
    defaultMessage: 'Could not change auto-rotation',
  },
  confirmReplaceCid: {
    id: 'telephony.supervisor.confirm.replaceCid',
    defaultMessage:
      'Replace {phone}? This buys a new number in the same area code (~$1.15), adds it to the pool and deactivates {phone}.',
  },
  cidReplaced: {
    id: 'telephony.supervisor.cidReplaced',
    defaultMessage: 'Replaced {oldNumber} → {newNumber} ({state}).',
  },
  cidReplaceFailed: {
    id: 'telephony.supervisor.cidReplaceFailed',
    defaultMessage: 'Could not replace number',
  },
  toastNoPolicyDial: {
    id: 'telephony.supervisor.toast.noPolicyDial',
    defaultMessage: 'PolicyDial is not enabled for this account.',
  },
  toastNoPhoneLogin: {
    id: 'telephony.supervisor.toast.noPhoneLogin',
    defaultMessage: 'No PolicyDial phone login is configured for this supervisor account.',
  },
  toastMonitorBusy: {
    id: 'telephony.supervisor.toast.monitorBusy',
    defaultMessage: 'Stop the active monitor session before starting a new one.',
  },
  toastMonitorStarted: {
    id: 'telephony.supervisor.toast.monitorStarted',
    defaultMessage: '{stage} started for {agent}. Answer in PolicyDial when it rings.',
  },
  toastMonitorFailed: {
    id: 'telephony.supervisor.toast.monitorFailed',
    defaultMessage: '{stage} failed',
  },
  toastMonitorEnded: {
    id: 'telephony.supervisor.toast.monitorEnded',
    defaultMessage: '{stage} ended. Supervisor agent restored.',
  },
  toastMonitorStopFailed: {
    id: 'telephony.supervisor.toast.monitorStopFailed',
    defaultMessage: 'Could not stop monitor cleanly',
  },
  toastMonitorStopWarning: {
    id: 'telephony.supervisor.toast.monitorStopWarning',
    defaultMessage: '{message}. Hang up in PolicyDial if the call is still active.',
  },
  confirmHangup: {
    id: 'telephony.supervisor.confirm.hangup',
    defaultMessage:
      'End the current call for {agent}? The agent stays logged in and returns to READY.',
  },
  toastCallEnded: {
    id: 'telephony.supervisor.toast.callEnded',
    defaultMessage: 'Call ended for {agent}.',
  },
  toastNotOnCall: {
    id: 'telephony.supervisor.toast.notOnCall',
    defaultMessage: '{agent} is not on a call.',
  },
  toastHangupFailed: {
    id: 'telephony.supervisor.toast.hangupFailed',
    defaultMessage: 'Could not hang up the call',
  },
  toastCampaignChanged: {
    id: 'telephony.supervisor.toast.campaignChanged',
    defaultMessage:
      '{agent} moved to {campaign}. They were logged out — they must reconnect PolicyDial to join the new campaign.',
  },
  toastCampaignChangeFailed: {
    id: 'telephony.supervisor.toast.campaignChangeFailed',
    defaultMessage: 'Failed to change campaign',
  },
  toastLoggedOut: {
    id: 'telephony.supervisor.toast.loggedOut',
    defaultMessage: '{count, plural, one {# agent} other {# agents}} logged out from PolicyDial.',
  },
  toastStillActive: {
    id: 'telephony.supervisor.toast.stillActive',
    defaultMessage:
      '{count, plural, one {# agent} other {# agents}} still appear active after logout.',
  },
  toastPaused: {
    id: 'telephony.supervisor.toast.paused',
    defaultMessage: '{count, plural, one {# agent} other {# agents}} paused in PolicyDial.',
  },
  toastResumed: {
    id: 'telephony.supervisor.toast.resumed',
    defaultMessage: '{count, plural, one {# agent} other {# agents}} resumed in PolicyDial.',
  },
  toastBulkFailures: {
    id: 'telephony.supervisor.toast.bulkFailures',
    defaultMessage: '{count, plural, one {# agent} other {# agents}} failed. First error: {error}',
  },
  agentFailure: {
    id: 'telephony.supervisor.agentFailure',
    defaultMessage: '{agent}: {message}',
  },

  // ── Pestaña de acceso a campañas ──
  accessTitle: {id: 'telephony.supervisor.access.title', defaultMessage: 'Campaign Access'},
  accessSubtitle: {
    id: 'telephony.supervisor.access.subtitle',
    defaultMessage: 'Assign which VICIdial campaigns each group / user (or everyone) can see.',
  },
  accessSummary: {
    id: 'telephony.supervisor.access.summary',
    defaultMessage: '{assigned}/{total} campaigns assigned',
  },
  accessEnforceLabel: {
    id: 'telephony.supervisor.access.enforceLabel',
    defaultMessage: 'Enforce campaign visibility',
  },
  accessEnforceOn: {id: 'telephony.supervisor.access.enforceOn', defaultMessage: 'ON'},
  accessEnforceOff: {
    id: 'telephony.supervisor.access.enforceOff',
    defaultMessage: 'off · everyone sees all',
  },
  accessEnforceNotice: {
    id: 'telephony.supervisor.access.enforceNotice',
    defaultMessage:
      'Enforcement is ON — a campaign with no assignment (and not "all") is hidden from non-admins.',
  },
  accessConfirmEnforce: {
    id: 'telephony.supervisor.access.confirmEnforce',
    defaultMessage:
      'Enforce campaign visibility? Non-admin agents/supervisors will ONLY see campaigns assigned to their groups/user (or marked "all"). Assign campaigns first, or agents will have no campaigns.',
  },
  accessColCampaign: {id: 'telephony.field.campaign', defaultMessage: 'Campaign'},
  // "Everyone" y no "All": la columna marca que la campaña la ve todo el mundo,
  // no un filtro de paginación (y así `audit` no la confunde con common.pagination.all).
  accessColAll: {id: 'telephony.supervisor.access.column.all', defaultMessage: 'Everyone'},
  accessColGroups: {id: 'telephony.supervisor.access.column.groups', defaultMessage: 'Groups'},
  accessColUsers: {id: 'common.field.users', defaultMessage: 'Users'},
  accessActiveBadge: {id: 'telephony.supervisor.access.activeBadge', defaultMessage: 'active'},
  accessTip: {
    id: 'telephony.supervisor.access.tip',
    defaultMessage:
      'Tip: hold Ctrl/Cmd (or Shift) to select multiple groups/users. "All" overrides the group/user lists.',
  },
  accessLoadFailed: {
    id: 'telephony.supervisor.access.loadFailed',
    defaultMessage: 'Failed to load campaign access',
  },
  accessSaved: {
    id: 'telephony.supervisor.access.saved',
    defaultMessage: 'Access saved for {campaign}.',
  },
  accessSaveFailed: {
    id: 'telephony.supervisor.access.saveFailed',
    defaultMessage: 'Could not save',
  },
  accessEnforceEnabled: {
    id: 'telephony.supervisor.access.enforceEnabled',
    defaultMessage: 'Campaign visibility enforcement ON.',
  },
  accessEnforceDisabled: {
    id: 'telephony.supervisor.access.enforceDisabled',
    defaultMessage: 'Enforcement OFF (everyone sees all).',
  },
  accessEnforceChangeFailed: {
    id: 'telephony.supervisor.access.enforceChangeFailed',
    defaultMessage: 'Could not change enforcement',
  },

  // ── Cabecera de la página ──
  accessRestrictedTitle: {
    id: 'telephony.supervisor.accessRestricted.title',
    defaultMessage: 'Access restricted',
  },
  accessRestrictedBody: {
    id: 'telephony.supervisor.accessRestricted.body',
    defaultMessage:
      'Your account does not have access to the PolicyDial supervisor dashboard. Ask an administrator to enable it from Users → PolicyDial Settings.',
  },
  pageTitle: {id: 'telephony.supervisor.pageTitle', defaultMessage: 'Telephony Supervisor'},
  backToIntegrations: {
    id: 'telephony.supervisor.backToIntegrations',
    defaultMessage: 'Back to integrations',
  },

  // ── Banner de supervisión activa ──
  monitorActive: {
    id: 'telephony.supervisor.monitorActive',
    defaultMessage: '{stage} active on {agent}',
  },
  monitorBannerHint: {
    id: 'telephony.supervisor.monitorBannerHint',
    defaultMessage:
      'Answer the incoming call in the PolicyDial drawer. Click Stop when you are done — it hangs up the supervisor leg and restores your previous agent status.',
  },
  stop: {id: 'telephony.supervisor.stop', defaultMessage: 'Stop'},
  stopping: {id: 'telephony.supervisor.stopping', defaultMessage: 'Stopping...'},

  // ── Pestañas ──
  tabLive: {id: 'telephony.supervisor.tab.live', defaultMessage: 'Live'},
  tabMetrics: {id: 'telephony.supervisor.tab.metrics', defaultMessage: 'Metrics'},
  tabCallLog: {id: 'telephony.supervisor.tab.callLog', defaultMessage: 'Call log'},
  tabDialerHealth: {id: 'telephony.supervisor.tab.dialerHealth', defaultMessage: 'Dialer health'},
  tabReports: {id: 'telephony.supervisor.tab.reports', defaultMessage: 'Reports'},
  tabCampaigns: {id: 'telephony.supervisor.tab.campaigns', defaultMessage: 'Campaigns'},
  tabDialerCampaigns: {
    id: 'telephony.supervisor.tab.dialerCampaigns',
    defaultMessage: 'PolicyDial Dialer',
  },
  tabAutoPush: {id: 'telephony.supervisor.tab.autoPush', defaultMessage: 'Auto-push'},
  tabAccess: {id: 'telephony.supervisor.tab.access', defaultMessage: 'Access'},

  // ── Tarjetas de resumen ──
  cardLoggedIn: {id: 'telephony.supervisor.card.loggedIn', defaultMessage: 'Agents logged in'},
  cardLoggedInHelper: {
    id: 'telephony.supervisor.card.loggedInHelper',
    defaultMessage:
      '{count, plural, one {# active PolicyDial session} other {# active PolicyDial sessions}}',
  },
  cardInCalls: {id: 'telephony.supervisor.card.inCalls', defaultMessage: 'Agents in calls'},
  cardInCallsHelper: {
    id: 'telephony.supervisor.card.inCallsHelper',
    defaultMessage: 'Live call sessions in progress',
  },
  cardWaiting: {id: 'telephony.supervisor.card.waiting', defaultMessage: 'Agents waiting'},
  cardWaitingHelper: {
    id: 'telephony.supervisor.card.waitingHelper',
    defaultMessage: 'READY and CLOSER states',
  },
  cardPaused: {id: 'telephony.supervisor.card.paused', defaultMessage: 'Paused agents'},
  cardPausedHelper: {
    id: 'telephony.supervisor.card.pausedHelper',
    defaultMessage: 'Agents temporarily unavailable',
  },
  cardInDispo: {id: 'telephony.supervisor.card.inDispo', defaultMessage: 'Agents in dispo'},
  cardInDispoHelper: {
    id: 'telephony.supervisor.card.inDispoHelper',
    defaultMessage: 'Wrap-up / disposition work',
  },
  cardDeadCalls: {id: 'telephony.supervisor.card.deadCalls', defaultMessage: 'Dead calls'},
  cardDeadCallsHelper: {
    id: 'telephony.supervisor.card.deadCallsHelper',
    defaultMessage: 'Sessions marked DEAD in PolicyDial',
  },

  // ── Tabla de agentes en vivo ──
  liveTitle: {id: 'telephony.supervisor.live.title', defaultMessage: 'Live PolicyDial'},
  liveSubtitle: {
    id: 'telephony.supervisor.live.subtitle',
    defaultMessage: 'Session counts and agent statuses come directly from PolicyDial.',
  },
  liveQueueCalls: {
    id: 'telephony.supervisor.live.queueCalls',
    defaultMessage: 'Global queue calls: {count}',
  },
  liveAudioHealthTitle: {
    id: 'telephony.supervisor.live.audioHealthTitle',
    defaultMessage: 'Live audio quality across agents on a call',
  },
  liveAudioHealth: {
    id: 'telephony.supervisor.live.audioHealth',
    defaultMessage:
      'Audio health: <dotGood>●</dotGood> {good} good · <dotFair>●</dotFair> {fair} fair · <dotPoor>●</dotPoor> {poor} poor',
  },
  liveAllUserGroups: {
    id: 'telephony.supervisor.live.allUserGroups',
    defaultMessage: 'All user groups',
  },
  liveAllCampaigns: {
    id: 'telephony.supervisor.live.allCampaigns',
    defaultMessage: 'All campaigns',
  },
  liveAgentsSelected: {
    id: 'telephony.supervisor.live.agentsSelected',
    defaultMessage: '{count, plural, one {# agent selected} other {# agents selected}}',
  },
  livePauseSelected: {
    id: 'telephony.supervisor.live.pauseSelected',
    defaultMessage: 'Pause Selected',
  },
  liveResumeSelected: {
    id: 'telephony.supervisor.live.resumeSelected',
    defaultMessage: 'Resume Selected',
  },
  liveForceLogout: {id: 'telephony.supervisor.live.forceLogout', defaultMessage: 'Force Logout'},
  liveColAgent: {id: 'common.field.agent', defaultMessage: 'Agent'},
  liveColSession: {id: 'telephony.supervisor.live.column.session', defaultMessage: 'Session'},
  liveColPauseReason: {
    id: 'telephony.supervisor.live.column.pauseReason',
    defaultMessage: 'Pause reason',
  },
  liveColTimeInState: {
    id: 'telephony.supervisor.live.column.timeInState',
    defaultMessage: 'Time in state',
  },
  liveColCallsToday: {
    id: 'telephony.supervisor.live.column.callsToday',
    defaultMessage: 'Calls today (PolicyDial)',
  },
  liveColAudioQuality: {
    id: 'telephony.supervisor.live.column.audioQuality',
    defaultMessage: 'Audio quality',
  },
  liveColBrowser: {id: 'telephony.supervisor.live.column.browser', defaultMessage: 'Browser'},
  liveSelectAllVisible: {
    id: 'telephony.supervisor.live.selectAllVisible',
    defaultMessage: 'Select all visible agents',
  },
  liveSelectAgent: {
    id: 'telephony.supervisor.live.selectAgent',
    defaultMessage: 'Select {agent}',
  },
  liveNoAgents: {
    id: 'telephony.supervisor.live.noAgents',
    defaultMessage: 'No agents currently logged in',
  },
  liveNoMatches: {
    id: 'telephony.supervisor.live.noMatches',
    defaultMessage: 'No live agents match the current filters',
  },
  liveChangeCampaignTitle: {
    id: 'telephony.supervisor.live.changeCampaignTitle',
    defaultMessage: 'Change campaign (logs the agent out to re-join)',
  },
  liveAudioTitle: {
    id: 'telephony.supervisor.live.audioTitle',
    defaultMessage: 'Codec {codec} · loss recv {recv}% / send {send}% · jitter {jitter} ms',
  },
  liveAudioNoCall: {
    id: 'telephony.supervisor.live.audioNoCall',
    defaultMessage: 'no active call',
  },
  liveAudioMetrics: {
    id: 'telephony.supervisor.live.audioMetrics',
    defaultMessage: '{loss}% · {jitter}ms',
  },
  liveBehaviorFlagTitle: {
    id: 'telephony.supervisor.live.behaviorFlagTitle',
    defaultMessage:
      'Network looks clean but calls keep ending in seconds — the agent likely cannot hear the customer (device/output problem). Ask them to run Audio & Device Check.',
  },
  liveWebphoneBrowserTitle: {
    id: 'telephony.supervisor.live.webphoneBrowserTitle',
    defaultMessage: 'Browser hosting the webphone (call audio)',
  },
  liveAppBrowserTitle: {
    id: 'telephony.supervisor.live.appBrowserTitle',
    defaultMessage: 'Other tab using PolicyTools (not the webphone)',
  },
  liveAppBrowser: {id: 'telephony.supervisor.live.appBrowser', defaultMessage: 'app: {label}'},
  liveWorking: {id: 'telephony.supervisor.live.working', defaultMessage: 'Working...'},
  liveMonitorTitle: {
    id: 'telephony.supervisor.live.monitorTitle',
    defaultMessage: 'Silent monitor',
  },
  liveWhisperTitle: {
    id: 'telephony.supervisor.live.whisperTitle',
    defaultMessage: 'Whisper to agent',
  },
  liveBargeTitle: {
    id: 'telephony.supervisor.live.bargeTitle',
    defaultMessage: 'Barge (two-way)',
  },
  liveHangupTitle: {
    id: 'telephony.supervisor.live.hangupTitle',
    defaultMessage: 'Force-end the current call (agent stays logged in)',
  },
  liveHangup: {id: 'telephony.supervisor.live.hangup', defaultMessage: 'Hang up call'},
  liveLogoutTitle: {
    id: 'telephony.supervisor.live.logoutTitle',
    defaultMessage: 'Force logout from PolicyDial',
  },
  liveLogout: {id: 'telephony.supervisor.live.logout', defaultMessage: 'Logout'},

  // ── Modal de cambio de campaña ──
  changeCampaignTitle: {
    id: 'telephony.supervisor.changeCampaign.title',
    defaultMessage: 'Change agent campaign',
  },
  changeCampaignBody: {
    id: 'telephony.supervisor.changeCampaign.body',
    defaultMessage: 'Move <b>{agent}</b> <muted>({user})</muted> from {from} → {to}',
  },
  changeCampaignWarnTitle: {
    id: 'telephony.supervisor.changeCampaign.warnTitle',
    defaultMessage: 'The agent will be logged out',
  },
  changeCampaignWarnBody: {
    id: 'telephony.supervisor.changeCampaign.warnBody',
    defaultMessage:
      "PolicyDial can't switch campaigns live, so the agent is logged out and must <b>reconnect PolicyDial</b> (one click) to join the new campaign. Any call in progress will be ended.",
  },
  changeCampaignMoving: {
    id: 'telephony.supervisor.changeCampaign.moving',
    defaultMessage: 'Moving…',
  },
  changeCampaignConfirm: {
    id: 'telephony.supervisor.changeCampaign.confirm',
    defaultMessage: 'Move & log out',
  },

  // ── Modal de acción masiva ──
  bulkTitleLogout: {
    id: 'telephony.supervisor.bulk.titleLogout',
    defaultMessage: 'Confirm Force Logout',
  },
  bulkTitlePause: {
    id: 'telephony.supervisor.bulk.titlePause',
    defaultMessage: 'Confirm Bulk Pause',
  },
  bulkTitleResume: {
    id: 'telephony.supervisor.bulk.titleResume',
    defaultMessage: 'Confirm Bulk Resume',
  },
  bulkDescLogout: {
    id: 'telephony.supervisor.bulk.descLogout',
    defaultMessage:
      'This will disconnect the selected PolicyDial sessions. Any active calls on those sessions may be dropped.',
  },
  bulkDescPause: {
    id: 'telephony.supervisor.bulk.descPause',
    defaultMessage: 'This will set the selected PolicyDial agents to a paused state.',
  },
  bulkDescResume: {
    id: 'telephony.supervisor.bulk.descResume',
    defaultMessage:
      'This will attempt to return the selected PolicyDial agents to an active state.',
  },
  bulkMoreAgents: {
    id: 'telephony.supervisor.bulk.moreAgents',
    defaultMessage: '+{count} more',
  },
  bulkRiskCount: {
    id: 'telephony.supervisor.bulk.riskCount',
    defaultMessage:
      '{count, plural, one {# selected agent is in a live call state} other {# selected agents are in live call states}}',
  },
  bulkNoRisk: {
    id: 'telephony.supervisor.bulk.noRisk',
    defaultMessage: 'No selected agents are currently in live call states',
  },
  bulkNoticeLogout: {
    id: 'telephony.supervisor.bulk.noticeLogout',
    defaultMessage:
      'Use this only when the selected agents need to be removed immediately or their PolicyDial sessions are stuck.',
  },
  bulkNoticePause: {
    id: 'telephony.supervisor.bulk.noticePause',
    defaultMessage:
      'Pause is useful when you need to temporarily stop multiple agents from receiving or placing calls.',
  },
  bulkNoticeResume: {
    id: 'telephony.supervisor.bulk.noticeResume',
    defaultMessage:
      'Resume will attempt to return the selected agents to an active PolicyDial state.',
  },

  // ── Pestaña Metrics ──
  metricsTitle: {id: 'telephony.supervisor.metrics.title', defaultMessage: 'PolicyTools Metrics'},
  metricsSubtitle: {
    id: 'telephony.supervisor.metrics.subtitle',
    defaultMessage: 'Historical analytics based on local call records and webhook sync.',
  },
  metricsWindowLocked: {
    id: 'telephony.supervisor.metrics.windowLocked',
    defaultMessage: 'Clear the date range to use a relative window',
  },
  metricsLastHours: {
    id: 'telephony.supervisor.metrics.lastHours',
    defaultMessage: '{count, plural, one {Last # hour} other {Last # hours}}',
  },
  metricsLastDays: {
    id: 'telephony.supervisor.metrics.lastDays',
    defaultMessage: '{count, plural, one {Last # day} other {Last # days}}',
  },
  metricsClearDatesTitle: {
    id: 'telephony.supervisor.metrics.clearDatesTitle',
    defaultMessage: 'Clear date range',
  },
  metricsClearDates: {
    id: 'telephony.supervisor.metrics.clearDates',
    defaultMessage: 'Clear dates',
  },
  metricsAllAgents: {id: 'telephony.supervisor.metrics.allAgents', defaultMessage: 'All agents'},
  metricsFunnelCalls: {id: 'telephony.supervisor.metrics.funnel.calls', defaultMessage: 'Calls'},
  metricsFunnelConversations: {
    id: 'telephony.supervisor.metrics.funnel.conversations',
    defaultMessage: 'Conversations (>{seconds}s)',
  },
  metricsFunnelDispositioned: {
    id: 'telephony.supervisor.metrics.funnel.dispositioned',
    defaultMessage: 'Dispositioned',
  },
  metricsFunnelSales: {id: 'telephony.supervisor.metrics.funnel.sales', defaultMessage: 'Sales'},
  metricsOfCalls: {
    id: 'telephony.supervisor.metrics.ofCalls',
    defaultMessage: '{percent} of calls',
  },
  metricsOfConversations: {
    id: 'telephony.supervisor.metrics.ofConversations',
    defaultMessage: '{percent} of conv.',
  },
  metricsOfDispositioned: {
    id: 'telephony.supervisor.metrics.ofDispositioned',
    defaultMessage: '{percent} of dispo',
  },
  metricsSaleRate: {id: 'telephony.supervisor.metrics.saleRate', defaultMessage: 'Sale rate'},
  metricsCallbackRate: {
    id: 'telephony.supervisor.metrics.callbackRate',
    defaultMessage: 'Callback rate',
  },
  metricsAvgTalkTime: {
    id: 'telephony.supervisor.metrics.avgTalkTime',
    defaultMessage: 'Avg talk time',
  },
  metricsOnRealConversations: {
    id: 'telephony.supervisor.metrics.onRealConversations',
    defaultMessage: 'on real conversations',
  },
  metricsCallsPerHour: {
    id: 'telephony.supervisor.metrics.callsPerHour',
    defaultMessage: 'Calls / hour',
  },
  metricsRatio: {id: 'telephony.supervisor.metrics.ratio', defaultMessage: '{a} / {b}'},
  metricsPerDayTitle: {
    id: 'telephony.supervisor.metrics.perDayTitle',
    defaultMessage: 'Conversations per day',
  },
  metricsColDay: {id: 'telephony.supervisor.metrics.column.day', defaultMessage: 'Day'},
  metricsColConversations: {
    id: 'telephony.supervisor.metrics.column.conversations',
    defaultMessage: 'Conversations',
  },
  metricsColTotalTime: {
    id: 'telephony.supervisor.metrics.column.totalTime',
    defaultMessage: 'Total time',
  },
  metricsColAvgPerCall: {
    id: 'telephony.supervisor.metrics.column.avgPerCall',
    defaultMessage: 'Avg / call',
  },
  metricsByAgent: {id: 'telephony.supervisor.metrics.byAgent', defaultMessage: 'By agent'},
  metricsByCampaign: {
    id: 'telephony.supervisor.metrics.byCampaign',
    defaultMessage: 'By campaign',
  },
  metricsColConvShort: {
    id: 'telephony.supervisor.metrics.column.convShort',
    defaultMessage: 'Conv.',
  },
  metricsColSalePct: {
    id: 'telephony.supervisor.metrics.column.salePct',
    defaultMessage: 'Sale %',
  },
  metricsColAvgTalk: {
    id: 'telephony.supervisor.metrics.column.avgTalk',
    defaultMessage: 'Avg talk',
  },
  metricsColCallsPerHour: {
    id: 'telephony.supervisor.metrics.column.callsPerHour',
    defaultMessage: 'C/h',
  },
  metricsNoData: {id: 'telephony.supervisor.metrics.noData', defaultMessage: 'No data'},
  metricsDispositions: {
    id: 'telephony.supervisor.metrics.dispositions',
    defaultMessage: 'Dispositions',
  },

  // ── Pestaña Call log ──
  callLogTitle: {
    id: 'telephony.supervisor.callLog.title',
    defaultMessage: 'Call log (recordings)',
  },
  callLogSubtitle: {
    id: 'telephony.supervisor.callLog.subtitle',
    defaultMessage:
      'Detailed calls straight from PolicyDial (real agent + duration). Press play to listen.',
  },
  callLogCustomer: {id: 'telephony.supervisor.callLog.customer', defaultMessage: 'Customer'},
  callLogCustomerPlaceholder: {
    id: 'telephony.supervisor.callLog.customerPlaceholder',
    defaultMessage: 'Customer name',
  },
  callLogResult: {id: 'telephony.supervisor.callLog.result', defaultMessage: 'Result'},
  callLogAllResults: {
    id: 'telephony.supervisor.callLog.allResults',
    defaultMessage: 'All results',
  },
  callLogView: {id: 'telephony.supervisor.callLog.view', defaultMessage: 'View'},
  callLogViewAll: {id: 'telephony.supervisor.callLog.viewAll', defaultMessage: 'All'},
  callLogViewMissed: {
    id: 'telephony.supervisor.callLog.viewMissed',
    defaultMessage: 'Missed',
  },
  callLogMissedBadge: {
    id: 'telephony.supervisor.callLog.missedBadge',
    defaultMessage: 'Missed',
  },
  callLogCallBack: {id: 'telephony.supervisor.callLog.callBack', defaultMessage: 'Call back'},
  callLogReturnedBy: {
    id: 'telephony.supervisor.callLog.returnedBy',
    defaultMessage: 'Returned by {name}',
  },
  callLogAlreadyReturnedBy: {
    id: 'telephony.supervisor.callLog.alreadyReturnedBy',
    defaultMessage: 'Already returned by {name}',
  },
  callLogColDateTime: {
    id: 'telephony.supervisor.callLog.column.dateTime',
    defaultMessage: 'Date / time',
  },
  callLogColSourcePhone: {
    id: 'telephony.supervisor.callLog.column.sourcePhone',
    defaultMessage: 'Source phone',
  },
  callLogColPhoneDestination: {
    id: 'telephony.supervisor.callLog.column.phoneDestination',
    defaultMessage: 'Phone destination',
  },
  callLogColDuration: {
    id: 'telephony.supervisor.callLog.column.duration',
    defaultMessage: 'Duration',
  },
  callLogColRecording: {
    id: 'telephony.supervisor.callLog.column.recording',
    defaultMessage: 'Recording',
  },
  callLogEmpty: {
    id: 'telephony.supervisor.callLog.empty',
    defaultMessage: 'No calls for the selected filters',
  },
  callLogHandled: {id: 'telephony.supervisor.callLog.handled', defaultMessage: 'Handled'},
  callLogRawStatus: {
    id: 'telephony.supervisor.callLog.rawStatus',
    defaultMessage: '({status})',
  },
  callLogNoRecording: {
    id: 'telephony.supervisor.callLog.noRecording',
    defaultMessage: 'No recording',
  },
  callLogPrev: {id: 'telephony.supervisor.callLog.prev', defaultMessage: '← Prev'},
  callLogNext: {id: 'telephony.supervisor.callLog.next', defaultMessage: 'Next →'},

  // ── Pestaña Dialer health ──
  dialerSubtitle: {
    id: 'telephony.supervisor.dialer.subtitle',
    defaultMessage: 'Real-time campaign / dialer status straight from PolicyDial.',
  },
  dialerAsOf: {id: 'telephony.supervisor.dialer.asOf', defaultMessage: 'As of {date}.'},
  dialerRefreshing: {
    id: 'telephony.supervisor.dialer.refreshing',
    defaultMessage: 'Refreshing…',
  },
  dialerNoCampaigns: {
    id: 'telephony.supervisor.dialer.noCampaigns',
    defaultMessage: 'No active campaigns.',
  },
  dialerDropPct: {id: 'telephony.supervisor.dialer.dropPct', defaultMessage: 'Drop %'},
  dialerTarget: {id: 'telephony.supervisor.dialer.target', defaultMessage: 'target {pct}%'},
  dialerDialLevel: {id: 'telephony.supervisor.dialer.dialLevel', defaultMessage: 'Dial level'},
  dialerAvgAgents: {id: 'telephony.supervisor.dialer.avgAgents', defaultMessage: 'Avg agents'},
  dialerDialableLeads: {
    id: 'telephony.supervisor.dialer.dialableLeads',
    defaultMessage: 'Dialable leads',
  },
  dialerHopper: {
    id: 'telephony.supervisor.dialer.hopper',
    defaultMessage: 'Leads in hopper',
  },
  dialerAgents: {id: 'common.field.agents', defaultMessage: 'Agents'},
  dialerAgentsLegend: {
    id: 'telephony.supervisor.dialer.agentsLegend',
    defaultMessage: 'ready / on call / paused',
  },
  dialerCallsToday: {
    id: 'telephony.supervisor.dialer.callsToday',
    defaultMessage: 'Calls today',
  },
  dialerAnswers: {id: 'telephony.supervisor.dialer.answers', defaultMessage: 'Answers'},
  dialerAgentCalls: {
    id: 'telephony.supervisor.dialer.agentCalls',
    defaultMessage: 'Agent calls',
  },
  dialerMachines: {id: 'telephony.supervisor.dialer.machines', defaultMessage: 'Machines'},
  dialerDrops: {id: 'telephony.supervisor.dialer.drops', defaultMessage: 'Drops'},
  dialerDialMethod: {
    id: 'telephony.supervisor.dialer.dialMethod',
    defaultMessage: 'Dial method',
  },
  dialerStatuses: {id: 'telephony.supervisor.dialer.statuses', defaultMessage: 'Statuses'},
  dialerOrder: {id: 'telephony.supervisor.dialer.order', defaultMessage: 'Order'},
  dialerHopperLevel: {
    id: 'telephony.supervisor.dialer.hopperLevel',
    defaultMessage: 'Hopper (min)',
  },
  dialerDlDiff: {id: 'telephony.supervisor.dialer.dlDiff', defaultMessage: 'DL diff'},
  dialerDropSec: {id: 'telephony.supervisor.dialer.dropSec', defaultMessage: 'Drop sec'},
  dialerTrunk: {
    id: 'telephony.supervisor.dialer.trunk',
    defaultMessage: 'Trunk short/fill',
  },
  dialerSettingLabel: {
    id: 'telephony.supervisor.dialer.settingLabel',
    defaultMessage: '{label}:',
  },
  dialerOverTarget: {
    id: 'telephony.supervisor.dialer.overTarget',
    defaultMessage: 'Drop % above target — dialer may be throttling.',
  },

  // ── Hub de informes ──
  reportsSubtitle: {
    id: 'telephony.supervisor.reports.subtitle',
    defaultMessage: 'PolicyDial reports — click one to open it.',
  },
  reportsSoon: {id: 'telephony.supervisor.reports.soon', defaultMessage: 'Soon'},
  reportsBack: {
    id: 'telephony.supervisor.reports.back',
    defaultMessage: '← Back to reports',
  },
  reportDateRange: {
    id: 'telephony.supervisor.report.dateRange',
    defaultMessage: '{from} → {to}.',
  },
  reportNoData: {id: 'telephony.supervisor.report.noData', defaultMessage: 'No data.'},
  reportSeconds: {id: 'telephony.supervisor.report.seconds', defaultMessage: '{value}s'},
  reportPercent: {id: 'telephony.supervisor.report.percent', defaultMessage: '{value}%'},
  reportPercentParen: {
    id: 'telephony.supervisor.report.percentParen',
    defaultMessage: '({percent}%)',
  },
  reportPercentOf: {
    id: 'telephony.supervisor.report.percentOf',
    defaultMessage: '{value} ({percent}%)',
  },
  reportBadgeCount: {
    id: 'telephony.supervisor.report.badgeCount',
    defaultMessage: '{label} {value}',
  },
  reportWindow: {id: 'telephony.field.window', defaultMessage: 'Window'},
  reportToday: {id: 'telephony.supervisor.report.today', defaultMessage: 'Today'},

  // ── Informe de rendimiento por agente ──
  perfSubtitle: {
    id: 'telephony.supervisor.perf.subtitle',
    defaultMessage:
      'Real per-agent activity from PolicyDial (talk time from recordings; dispositions as entered).',
  },
  perfUserGroup: {id: 'telephony.supervisor.perf.userGroup', defaultMessage: 'User group'},
  perfAllGroups: {id: 'telephony.supervisor.perf.allGroups', defaultMessage: 'All groups'},
  perfNoActivity: {
    id: 'telephony.supervisor.perf.noActivity',
    defaultMessage: 'No agent activity in this range.',
  },
  perfColRealConvos: {
    id: 'telephony.supervisor.perf.column.realConvos',
    defaultMessage: 'Real convos',
  },
  perfColTalkTime: {
    id: 'telephony.supervisor.perf.column.talkTime',
    defaultMessage: 'Talk time',
  },
  perfColDisposed: {
    id: 'telephony.supervisor.perf.column.disposed',
    defaultMessage: 'Disposed',
  },
  perfColPauseTime: {
    id: 'telephony.supervisor.perf.column.pauseTime',
    defaultMessage: 'Pause time',
  },
  perfColPauseReasons: {
    id: 'telephony.supervisor.perf.column.pauseReasons',
    defaultMessage: 'Pause reasons',
  },
  perfNodispNote: {
    id: 'telephony.supervisor.perf.nodispNote',
    defaultMessage:
      '{badge} = connected but the agent entered no disposition. A low "Disposed %" means the agent isn\u2019t dispositioning their calls.',
  },

  // ── Informe de llamadas salientes ──
  outboundSubtitle: {
    id: 'telephony.supervisor.outbound.subtitle',
    defaultMessage: 'Outbound call totals from PolicyDial.',
  },
  outboundTotalCalls: {
    id: 'telephony.supervisor.outbound.totalCalls',
    defaultMessage: 'Total calls',
  },
  outboundAvgLength: {
    id: 'telephony.supervisor.outbound.avgLength',
    defaultMessage: 'Avg length',
  },
  outboundHumanAnswers: {
    id: 'telephony.supervisor.outbound.humanAnswers',
    defaultMessage: 'Human answers',
  },
  outboundAvgTalkHa: {
    id: 'telephony.supervisor.outbound.avgTalkHa',
    defaultMessage: 'Avg talk (HA)',
  },
  outboundDropsOfAnswers: {
    id: 'telephony.supervisor.outbound.dropsOfAnswers',
    defaultMessage: 'Drops (of answers)',
  },
  outboundHangupReasons: {
    id: 'telephony.supervisor.outbound.hangupReasons',
    defaultMessage: 'Hangup reasons',
  },

  // ── Informe de listas ──
  listsSubtitle: {
    id: 'telephony.supervisor.lists.subtitle',
    defaultMessage: 'Contact and sale performance per list.',
  },
  listsNoActivity: {
    id: 'telephony.supervisor.lists.noActivity',
    defaultMessage: 'No list activity in this range.',
  },
  listsColList: {id: 'telephony.field.list', defaultMessage: 'List'},
  listsColContacts: {
    id: 'telephony.supervisor.lists.column.contacts',
    defaultMessage: 'Contacts',
  },
  listsColContactPct: {
    id: 'telephony.supervisor.lists.column.contactPct',
    defaultMessage: 'Contact %',
  },

  // ── Informe de entrantes ──
  inboundSubtitle: {
    id: 'telephony.supervisor.inbound.subtitle',
    defaultMessage: 'Inbound volume & service level',
  },
  inboundSlHint: {
    id: 'telephony.supervisor.inbound.slHint',
    defaultMessage: '(SL = answered within {seconds}s).',
  },
  inboundNoCalls: {
    id: 'telephony.supervisor.inbound.noCalls',
    defaultMessage: 'No inbound calls in this range.',
  },
  inboundAnswered: {id: 'telephony.supervisor.inbound.answered', defaultMessage: 'Answered'},
  inboundAbandoned: {
    id: 'telephony.supervisor.inbound.abandoned',
    defaultMessage: 'Abandoned',
  },
  inboundAvgWait: {id: 'telephony.supervisor.inbound.avgWait', defaultMessage: 'Avg wait'},
  inboundColAssignmentGroup: {
    id: 'telephony.supervisor.inbound.column.assignmentGroup',
    defaultMessage: 'Assignment group',
  },
  inboundServiceLevel: {
    id: 'telephony.supervisor.inbound.serviceLevel',
    defaultMessage: 'Service level',
  },
  inboundColInGroup: {
    id: 'telephony.supervisor.inbound.column.inGroup',
    defaultMessage: 'In-group',
  },

  // ── Informe de contactabilidad ──
  contactSubtitle: {
    id: 'telephony.supervisor.contact.subtitle',
    defaultMessage:
      'Human-to-agent contact, answering-machine and drop rates by US state. State from the lead, area code as fallback.',
  },
  contactNoCalls: {
    id: 'telephony.supervisor.contact.noCalls',
    defaultMessage: 'No calls in this window.',
  },
  contactDials: {id: 'telephony.supervisor.contact.dials', defaultMessage: 'Dials'},
  contactHumanToAgent: {
    id: 'telephony.supervisor.contact.humanToAgent',
    defaultMessage: 'Contact (human→agent)',
  },
  contactAnsweringMachine: {
    id: 'telephony.supervisor.contact.answeringMachine',
    defaultMessage: 'Answering machine',
  },
  contactDropped: {id: 'telephony.supervisor.contact.dropped', defaultMessage: 'Dropped'},
  contactColContact: {id: 'common.field.contact', defaultMessage: 'Contact'},
  contactColMachinePct: {
    id: 'telephony.supervisor.contact.column.machinePct',
    defaultMessage: 'Machine %',
  },
  contactNote: {
    id: 'telephony.supervisor.contact.note',
    defaultMessage:
      '<b>Contact</b> = a human reached an agent (agent disposition). <b>Machine</b> = AMD detected an answering machine / voicemail. A high machine % on a state means the leads for that state are mostly machines.',
  },

  // ── Informe de salud de Caller ID ──
  cidSubtitle: {
    id: 'telephony.supervisor.cid.subtitle',
    defaultMessage:
      'Outbound answer & failure rate per number. A number with a low answer rate and high failures (vs the pool) is likely carrier-flagged and should be rotated out.',
  },
  cidAutorotateLabel: {
    id: 'telephony.supervisor.cid.autorotateLabel',
    defaultMessage: 'Auto-rotate burned',
  },
  cidAutorotateOn: {
    id: 'telephony.supervisor.cid.autorotateOn',
    defaultMessage: 'on · max {count}/day',
  },
  cidRebuildTitle: {
    id: 'telephony.supervisor.cid.rebuildTitle',
    defaultMessage: 'Recompile usage from Twilio for this window (runs in the background)',
  },
  cidQueuing: {id: 'telephony.supervisor.cid.queuing', defaultMessage: 'Queuing…'},
  cidRebuild: {id: 'telephony.supervisor.cid.rebuild', defaultMessage: 'Rebuild data'},
  cidBuildingNote: {
    id: 'telephony.supervisor.cid.buildingNote',
    defaultMessage:
      'Compiled in the background from Twilio call logs. New numbers or a fresh deploy may show nothing until the first build completes.',
  },
  cidBuildingNoteAdmin: {
    id: 'telephony.supervisor.cid.buildingNoteAdmin',
    defaultMessage:
      'Compiled in the background from Twilio call logs. New numbers or a fresh deploy may show nothing until the first build completes — click "Rebuild data", then Apply again in ~1 min.',
  },
  cidNoOutbound: {
    id: 'telephony.supervisor.cid.noOutbound',
    defaultMessage: 'No outbound calls in this window.',
  },
  cidStillBuilding: {
    id: 'telephony.supervisor.cid.stillBuilding',
    defaultMessage: 'Data may still be building — click "Rebuild data", then Apply again shortly.',
  },
  cidPoolAnswerRate: {
    id: 'telephony.supervisor.cid.poolAnswerRate',
    defaultMessage: 'Pool answer rate',
  },
  cidPoolFailRate: {
    id: 'telephony.supervisor.cid.poolFailRate',
    defaultMessage: 'Pool failure rate',
  },
  cidBurned: {id: 'telephony.supervisor.cid.burned', defaultMessage: 'Burned'},
  cidWatch: {id: 'telephony.supervisor.cid.watch', defaultMessage: 'Watch'},
  cidHealthy: {id: 'telephony.supervisor.cid.healthy', defaultMessage: 'Healthy'},
  cidColAnswerPct: {
    id: 'telephony.supervisor.cid.column.answerPct',
    defaultMessage: 'Answer %',
  },
  cidColFailPct: {id: 'telephony.supervisor.cid.column.failPct', defaultMessage: 'Fail %'},
  cidColHealth: {id: 'telephony.supervisor.cid.column.health', defaultMessage: 'Health'},
  cidRotatedOutBadge: {
    id: 'telephony.supervisor.cid.rotatedOutBadge',
    defaultMessage: 'pool · rotated out',
  },
  cidReplaceTitle: {
    id: 'telephony.supervisor.cid.replaceTitle',
    defaultMessage:
      'Buy a replacement in the same area code, add it to the pool and deactivate {phone}.',
  },
  cidReplacing: {id: 'telephony.supervisor.cid.replacing', defaultMessage: 'Replacing…'},
  cidReplace: {id: 'telephony.supervisor.cid.replace', defaultMessage: 'Replace'},
  cidRotatedOut: {id: 'telephony.supervisor.cid.rotatedOut', defaultMessage: 'Rotated out'},
  cidNotRotatable: {
    id: 'telephony.supervisor.cid.notRotatable',
    defaultMessage: 'Not rotatable',
  },
  cidWhyRotatedOut: {
    id: 'telephony.supervisor.cid.whyRotatedOut',
    defaultMessage: 'Already rotated out of the pool — it is no longer dialing.',
  },
  cidWhyPolicyDial: {
    id: 'telephony.supervisor.cid.whyPolicyDial',
    defaultMessage: 'Agent (PolicyDial) number — rotation only touches local-presence pool CIDs.',
  },
  cidWhyOther: {
    id: 'telephony.supervisor.cid.whyOther',
    defaultMessage:
      'Not an active local-presence pool CID — its Caller ID is set elsewhere (campaign / agent), so it cannot be rotated from here.',
  },
  cidFootnote: {
    id: 'telephony.supervisor.cid.footnote',
    defaultMessage:
      'Numbers with fewer than 10 dials in the window are hidden (not enough data). <b>Burned</b> = answer rate < 15% or failure rate > 50%. Consider releasing burned numbers from the pool.',
  },

  // ── Reusadas de common.* ──
  name: {id: 'common.field.name', defaultMessage: 'Name'},
  status: {id: 'common.field.status', defaultMessage: 'Status'},
  group: {id: 'common.field.group', defaultMessage: 'Group'},
  actions: {id: 'common.field.actions', defaultMessage: 'Actions'},
  back: {id: 'common.action.back', defaultMessage: 'Back'},
  refresh: {id: 'common.action.refresh', defaultMessage: 'Refresh'},
  close: {id: 'common.action.close', defaultMessage: 'Close'},
  cancel: {id: 'common.action.cancel', defaultMessage: 'Cancel'},
  clearFilters: {id: 'common.action.clearFilters', defaultMessage: 'Clear filters'},
  loading: {id: 'common.loading', defaultMessage: 'Loading...'},
  colNumber: {id: 'common.field.number', defaultMessage: 'Number'},
  type: {id: 'common.field.type', defaultMessage: 'Type'},
  state: {id: 'common.field.state', defaultMessage: 'State'},
  action: {id: 'common.field.action', defaultMessage: 'Action'},
  apply: {id: 'common.action.apply', defaultMessage: 'Apply'},
  agent: {id: 'common.field.agent', defaultMessage: 'Agent'},
  phone: {id: 'common.field.phone', defaultMessage: 'Phone'},
  total: {id: 'common.field.total', defaultMessage: 'Total'},
  dateFrom: {id: 'common.field.dateFrom', defaultMessage: 'From date'},
  dateTo: {id: 'common.field.dateTo', defaultMessage: 'To date'},
  showingRange: {
    id: 'common.pagination.showingRange',
    defaultMessage: 'Showing {start}–{end} of {total}',
  },
  save: {id: 'common.action.save', defaultMessage: 'Save'},
})

/** Ventanas relativas del filtro de Métricas. `hours` es lo que viaja a la API. */
export const METRICS_WINDOWS = [
  {hours: 1, unit: 'hours' as const, count: 1},
  {hours: 4, unit: 'hours' as const, count: 4},
  {hours: 8, unit: 'hours' as const, count: 8},
  {hours: 24, unit: 'hours' as const, count: 24},
  {hours: 72, unit: 'days' as const, count: 3},
  {hours: 168, unit: 'days' as const, count: 7},
]

/** El primer escalón del embudo siempre es el 100 %: es una cifra, no texto. */
export const FULL_PERCENT = '100%'

/** Separadores y cifras sueltas que el rule marca dentro del JSX. */
export const SLASH_SEPARATOR = ' / '
export const ARROW_RIGHT = '→'

/** Comillas alrededor de un dato. Izado a helper: el rule marca las comillas sueltas en JSX. */
export const quoted = (value: string) => `"${value}"`

/** Parentesis alrededor de un dato, por el mismo motivo que `quoted`. */
export const parenthesized = (value: string) => `(${value})`
export const ZERO = '0'

/** Une las partes de un subtítulo saltándose las vacías, sin dejar dobles espacios. */
export const joinText = (...parts: Array<string | null | undefined | false>) =>
  parts.filter(Boolean).join(' ')

/** "ada01 — Ada Lovelace" en el selector de agentes del registro de llamadas. */
export const agentOptionLabel = (user: string, fullName?: string) =>
  fullName ? `${user} — ${fullName}` : user

/** Ventanas en días de los informes de contactabilidad y de salud de Caller ID. */
export const REPORT_DAY_WINDOWS = [7, 30, 90]

/** `NODISP` es un código de disposición de VICIdial: no se traduce. */
export const NODISP = 'NODISP'

/** "0001 — Ventas" en el selector de campañas del marcador. */
export const dialerCampaignOption = (id: string, name: string) => `${id} — ${name}`

/** Calidad de audio de la llamada en curso. */
const AUDIO_QUALITY = defineMessages({
  good: {id: 'telephony.supervisor.audioQuality.good', defaultMessage: 'Good'},
  fair: {id: 'telephony.supervisor.audioQuality.fair', defaultMessage: 'Fair'},
  poor: {id: 'telephony.supervisor.audioQuality.poor', defaultMessage: 'Poor'},
  idle: {id: 'telephony.supervisor.audioQuality.idle', defaultMessage: 'Idle'},
})

/** Una calidad nueva del backend se muestra en crudo en vez de desaparecer. */
export const audioQualityLabel = (intl: IntlShape, quality: string): string => {
  const descriptor = AUDIO_QUALITY[quality as keyof typeof AUDIO_QUALITY]
  return descriptor ? intl.formatMessage(descriptor) : quality
}

/** Flechas del encabezado ordenable: símbolos, no texto. */
export const SORT_ASC = '▲'
export const SORT_DESC = '▼'
export const SORT_NONE = '↕'

/** "Ada Lovelace (ada01)" — el usuario solo se añade si difiere del nombre. */
export const agentWithUser = (name: string, user: string) =>
  name && name !== user ? `${name} (${user})` : name || user

/**
 * Menú del hub de informes (al estilo "Server Stats and Reports" de VICIdial).
 * Vive a nivel de módulo, así que guarda descriptores y se formatea en render.
 */
const REPORT = defineMessages({
  agentperfTitle: {
    id: 'telephony.supervisor.report.agentperf.title',
    defaultMessage: 'Agent Performance',
  },
  agentperfDesc: {
    id: 'telephony.supervisor.report.agentperf.desc',
    defaultMessage: 'Per-agent calls, real conversations, talk time, sales and disposition mix.',
  },
  outboundTitle: {
    id: 'telephony.supervisor.report.outbound.title',
    defaultMessage: 'Outbound Calling Report',
  },
  outboundDesc: {
    id: 'telephony.supervisor.report.outbound.desc',
    defaultMessage:
      "Today's outbound totals: calls, human answers, machines, drops, hangup reasons.",
  },
  listsTitle: {
    id: 'telephony.supervisor.report.lists.title',
    defaultMessage: 'Lists / Lead Source',
  },
  listsDesc: {
    id: 'telephony.supervisor.report.lists.desc',
    defaultMessage: 'Contact and sale performance by list / lead source.',
  },
  inboundTitle: {
    id: 'telephony.supervisor.report.inbound.title',
    defaultMessage: 'Inbound Report',
  },
  inboundDesc: {
    id: 'telephony.supervisor.report.inbound.desc',
    defaultMessage: 'Inbound volume, service level, answered vs abandoned and wait times.',
  },
  contactabilityTitle: {
    id: 'telephony.supervisor.report.contactability.title',
    defaultMessage: 'Contactability by State',
  },
  contactabilityDesc: {
    id: 'telephony.supervisor.report.contactability.desc',
    defaultMessage: 'Contact rate, answering-machine rate, drops and sales grouped by US state.',
  },
  cidhealthTitle: {
    id: 'telephony.supervisor.report.cidhealth.title',
    defaultMessage: 'Caller ID Health',
  },
  cidhealthDesc: {
    id: 'telephony.supervisor.report.cidhealth.desc',
    defaultMessage:
      'Per-number answer and failure rates to spot carrier-flagged / burned Caller IDs.',
  },
})

/** Agrupador del menú de informes. */
const REPORT_GROUP = defineMessages({
  Agents: {id: 'common.field.agents', defaultMessage: 'Agents'},
  Outbound: {id: 'telephony.supervisor.report.group.outbound', defaultMessage: 'Outbound'},
  Inbound: {id: 'telephony.supervisor.report.group.inbound', defaultMessage: 'Inbound'},
})

export const reportGroupLabel = (intl: IntlShape, group: string): string => {
  const descriptor = REPORT_GROUP[group as keyof typeof REPORT_GROUP]
  return descriptor ? intl.formatMessage(descriptor) : group
}

/** Título de cada informe, para reusarlo en la cabecera de su vista abierta. */
export const REPORT_TITLE = {
  agentperf: REPORT.agentperfTitle,
  outbound: REPORT.outboundTitle,
  lists: REPORT.listsTitle,
  inbound: REPORT.inboundTitle,
  contactability: REPORT.contactabilityTitle,
  cidhealth: REPORT.cidhealthTitle,
}

export const REPORT_MENU = [
  {
    key: 'agentperf',
    group: 'Agents',
    title: REPORT.agentperfTitle,
    desc: REPORT.agentperfDesc,
    icon: 'bi bi-person-badge',
    available: true,
  },
  {
    key: 'outbound',
    group: 'Outbound',
    title: REPORT.outboundTitle,
    desc: REPORT.outboundDesc,
    icon: 'bi bi-telephone-outbound',
    available: true,
  },
  {
    key: 'lists',
    group: 'Outbound',
    title: REPORT.listsTitle,
    desc: REPORT.listsDesc,
    icon: 'bi bi-list-ul',
    available: true,
  },
  {
    key: 'inbound',
    group: 'Inbound',
    title: REPORT.inboundTitle,
    desc: REPORT.inboundDesc,
    icon: 'bi bi-telephone-inbound',
    available: true,
  },
  {
    key: 'contactability',
    group: 'Outbound',
    title: REPORT.contactabilityTitle,
    desc: REPORT.contactabilityDesc,
    icon: 'bi bi-geo-alt',
    available: true,
  },
  {
    key: 'cidhealth',
    group: 'Outbound',
    title: REPORT.cidhealthTitle,
    desc: REPORT.cidhealthDesc,
    icon: 'bi bi-shield-exclamation',
    available: true,
  },
]
